/**
 * WeeklyReportPage — The Weekly Read.
 *
 * Residence Journal · frozen design system.
 * A supportive, non-judgemental read of the household's week.
 * Presentation only — every derivation, testid and handler preserved.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { useWeeklyReset, WeeklyResetModal } from "./WeeklyReset";
import {
  getWeekRange, getMoodTrendSummary, getMemberMoodSummary,
  getCleaningSummary, getChoreSummary, getSwapSummary,
  getLoadBalanceSummary, getWeeklyAraInsights, getSuggestedNextWeekFocus,
} from "./utils/weeklyReportUtils";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  Caption, ModuleMark, TextLink, PillButton, Colophon,
  EmptyRoom, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

export default function WeeklyReportPage() {
  const navigate = useNavigate();
  const state = useHomeOSState();
  const weeklyReset = useWeeklyReset();
  const { phase } = useLightPhase();
  const [resetOpen, setResetOpen] = useState(false);

  const week = useMemo(() => getWeekRange(), []);
  const mood = useMemo(() => getMoodTrendSummary(state, week), [state, week]);
  const memberMoods = useMemo(() => getMemberMoodSummary(state, week), [state, week]);
  const cleaning = useMemo(() => getCleaningSummary(state, week), [state, week]);
  const chores = useMemo(() => getChoreSummary(state, week), [state, week]);
  const swaps = useMemo(() => getSwapSummary(state, week), [state, week]);
  const load = useMemo(() => getLoadBalanceSummary(state, week), [state, week]);
  const insights = useMemo(() => getWeeklyAraInsights(state, week), [state, week]);
  const focuses = useMemo(() => getSuggestedNextWeekFocus(state, week), [state, week]);

  const noData = mood.total === 0 && swaps.count === 0 && cleaning.completedCount === 0;

  let focal = "The household shared the load well this week.";
  if (noData) {
    focal = "The week is quiet — the report grows richer as the residence is used.";
  } else if ((mood.counts.red + mood.counts.blue) > (mood.counts.yellow + mood.counts.green) && mood.total > 0) {
    focal = "This week held some heavier moments. Small steps kept it light.";
  } else if (swaps.count >= 2) {
    focal = "Teamwork carried this week — help arrived without being asked.";
  }

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="weekly-report-page">
      <PageHeading
        module="today"
        room="This week"
        title="The week just past"
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setResetOpen(true)} testid="weekly-report-open-reset">
              Weekly reset
            </PillButton>
            <TextLink onClick={() => window.print()} testid="weekly-report-print">Print</TextLink>
            <TextLink onClick={() => navigate('/home-os')} testid="weekly-report-back">Return to Foyer</TextLink>
          </>
        }
      />

      {noData ? (
        <EmptyRoom
          purpose="This week"
          sentence="A quiet week. ARA will compose richer trends as the residence is used through the week."
          actionLabel="Open the Foyer"
          onAction={() => navigate('/home-os')}
          testid="weekly-report-empty"
        />
      ) : (
        <>
          {insights && insights.length > 0 && (
            <EditorialSection
              moduleMark={{ module: 'today', label: 'What ARA noticed' }}
              title="Judgement, not data"
              testid="weekly-report-insights"
            >
              <ul>
                {insights.map((line, i) => (
                  <li
                    key={i}
                    className={`py-4 ${i === 0 ? 'border-t' : ''} border-b`}
                    style={{ borderColor: 'var(--ara-line-soft)' }}
                    data-testid={`weekly-insight-${i}`}
                  >
                    <p className="font-display italic text-[15px] sm:text-[16px] leading-[1.45]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                      {typeof line === 'string' ? line : line.text || line.message || ''}
                    </p>
                  </li>
                ))}
              </ul>
            </EditorialSection>
          )}

          {mood.total > 0 && (
            <EditorialSection
              moduleMark={{ module: 'health', label: 'Household mood' }}
              title="How the week felt"
              testid="weekly-report-mood"
            >
              <div className="grid grid-cols-2 gap-6">
                <MoodFigure label="Steady moments" value={mood.counts.green} testid="mood-green" />
                <MoodFigure label="Light lifts"     value={mood.counts.yellow} testid="mood-yellow" />
                <MoodFigure label="Wobbles"         value={mood.counts.blue}   testid="mood-blue" />
                <MoodFigure label="Heavier days"    value={mood.counts.red}    testid="mood-red" />
              </div>
              {memberMoods && memberMoods.length > 0 && (
                <div className="mt-8">
                  <Caption>By person</Caption>
                  <div className="mt-3">
                    {memberMoods.map((m, i, arr) => (
                      <div
                        key={m.member || i}
                        className={`py-3 flex items-baseline justify-between gap-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                        style={{ borderColor: 'var(--ara-line-soft)' }}
                        data-testid={`weekly-member-mood-${i}`}
                      >
                        <p className="text-[15px] leading-tight" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                          {m.member || 'Household'}
                        </p>
                        <Caption>{m.summary || m.text || '—'}</Caption>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </EditorialSection>
          )}

          {(cleaning.completedCount > 0 || chores.completedCount > 0) && (
            <EditorialSection
              moduleMark={{ module: 'cleaning', label: 'Rhythms' }}
              title="What was kept up"
              testid="weekly-report-rhythms"
            >
              <div className="grid grid-cols-2 gap-6">
                <Figure label="Cleaning done"  value={String(cleaning.completedCount || 0)} testid="rhythm-cleaning" />
                <Figure label="Chores done"    value={String(chores.completedCount || 0)}   testid="rhythm-chores" />
                <Figure label="Swaps"          value={String(swaps.count || 0)}              testid="rhythm-swaps" />
                <Figure label="Load balance"   value={load?.summary || 'Balanced'}           testid="rhythm-load" />
              </div>
            </EditorialSection>
          )}

          {focuses && focuses.length > 0 && (
            <EditorialSection
              moduleMark={{ module: 'goals', label: 'Next week' }}
              title="A gentle focus"
              testid="weekly-report-focus"
            >
              <ul>
                {focuses.map((f, i) => (
                  <li
                    key={i}
                    className={`py-4 ${i === 0 ? 'border-t' : ''} border-b`}
                    style={{ borderColor: 'var(--ara-line-soft)' }}
                    data-testid={`weekly-focus-${i}`}
                  >
                    <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                      {typeof f === 'string' ? f : f.title || f.text || ''}
                    </p>
                    {f && typeof f === 'object' && (f.hint || f.detail) && (
                      <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                        {f.hint || f.detail}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </EditorialSection>
          )}
        </>
      )}

      <Colophon>ARA · Weekly read · {week?.label || ''}</Colophon>

      <WeeklyResetModal
        open={resetOpen || (weeklyReset?.open && weeklyReset?.showModal)}
        onClose={() => { setResetOpen(false); weeklyReset?.close?.(); }}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

function MoodFigure({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <Caption>{label}</Caption>
      <p className="mt-1 text-[26px] leading-none tabular-nums" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
        {value}
      </p>
    </div>
  );
}

function Figure({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <Caption>{label}</Caption>
      <p className="mt-1 text-[22px] leading-tight tabular-nums" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
        {value}
      </p>
    </div>
  );
}

/* Preserve import references for utility usage */
void ModuleMark;
